"use client";

import { useCallback, useMemo, useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePlayer } from "@/contexts/player-context";
import { useYouTubeAccessibility } from "@/hooks/useYouTubeAccessibility";
import { useUser } from "@/lib/context/user-context";
import { useI18n } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import useSWR from "swr";

interface YouTubeVideo {
  videoId: string;
  title: string;
  author: string;
  thumbnail: string;
  ownerAvatarUrl?: string;
  ownerProfileUrl?: string;
}

const FEATURED_YOUTUBE_VIDEO_IDS = ["OZugGTyBevY", "F1vBXPi6jlE", "FmJBY_SynY8"];

// 批量获取 YouTube 视频元数据的 fetcher
const fetchYoutubeMetadata = async (videoIds: string[]): Promise<YouTubeVideo[]> => {
  const results = await Promise.all(
    videoIds.map(async (videoId) => {
      try {
        const response = await fetch(`/api/youtube/metadata?videoId=${videoId}`);
        if (!response.ok) {
          throw new Error(`Failed to load metadata: ${response.status}`);
        }
        const payload = await response.json();
        if (payload?.success && payload?.data) {
          return payload.data as YouTubeVideo;
        }
      } catch (error) {
        console.error("[YouTube Section] failed to fetch metadata", error);
      }
      return null;
    })
  );
  return results.filter((item): item is YouTubeVideo => Boolean(item));
};

export default function YouTubeSection() {
  const { t } = useI18n();
  const {
    setCurrentEpisode,
    showPlayer,
    openFullPlayer,
    closeFullPlayer,
    setIsLoading: setPlayerLoading,
    setCleanReadData,
  } = usePlayer();
  const { session } = useUser();
  const { isAccessible: isYouTubeAccessible } = useYouTubeAccessibility();
  const isChinaRegion = false;
  
  const isMobile = useIsMobile();
  
  // 使用 mounted 状态避免 hydration 不匹配
  // 服务端和客户端初始渲染都返回 null，等客户端 mounted 后再根据实际值渲染
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const canAccessYouTube = isMounted && !isChinaRegion && isYouTubeAccessible;
  
  // 只在移动端启用直接播放，桌面端跳转到 episodes 页面
  const allowDirectPlay = isMounted && isMobile;

  // 使用 SWR 缓存 YouTube 视频数据
  // 使用稳定的 key，避免数组引用变化导致重复请求
  const swrKey = useMemo(
    () => (canAccessYouTube ? `youtube-featured-${FEATURED_YOUTUBE_VIDEO_IDS.join(",")}` : null),
    [canAccessYouTube]
  );

  const { data: youtubeVideos = [], isLoading: isLoadingYoutube } = useSWR(
    swrKey,
    () => fetchYoutubeMetadata(FEATURED_YOUTUBE_VIDEO_IDS),
    {
      // 这些数据几乎不变，使用更激进的缓存策略
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 600000, // 10分钟去重
      // 缓存数据 24 小时内有效
      refreshInterval: 0, // 不自动刷新
    }
  );

  // 获取 episode 页面 URL
  const getEpisodeUrl = useCallback((videoId: string) => {
    return `/episodes/${videoId}?videoId=${videoId}`;
  }, []);

  // 所有 hooks 必须在条件返回之前调用（React Hooks 规则）
  const handlePlayYouTubeVideo = useCallback(
    async (video: YouTubeVideo) => {
      showPlayer();
      openFullPlayer();
      setPlayerLoading(true);

      const shareId = video.videoId;
      const youtubeUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
      const placeholder = {
        shareId,
        sourceType: "youtube",
        videoId: video.videoId,
        videoUrl: youtubeUrl,
        origin: "video",
        title: video.title,
        author: video.author,
        thumbnail: video.thumbnail,
        ownerAvatarUrl: video.ownerAvatarUrl,
        ownerProfileUrl: video.ownerProfileUrl,
        hydrationPending: true,
        isHydrationPlaceholder: true,
        content: {
          shareId,
          title: video.title,
          author: video.author,
          thumbnail: video.thumbnail,
          ownerAvatarUrl: video.ownerAvatarUrl,
          ownerProfileUrl: video.ownerProfileUrl,
          videoId: video.videoId,
          videoUrl: youtubeUrl,
          origin: "video",
          hydrationPending: true,
          isHydrationPlaceholder: true,
        },
      };

      setCurrentEpisode(placeholder);
      setCleanReadData(null);

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      const token =
        session?.access_token ??
        (typeof window !== "undefined"
          ? window.localStorage.getItem("token")
          : null);
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      try {
        const response = await fetch(`/api/episodes/${shareId}`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const episodeData = await response.json();
          const shareData = (episodeData?.content ?? {}) as Record<string, any>;
          const shareDataContent = (shareData?.content ?? {}) as Record<string, any>;
          
          // 🔥 Keep the original author/owner info if the API returns "Unknown" or defaults
          // This happens when the episode is not cached and falls back to default metadata
          const resolvedAuthor = (shareData.author === 'Unknown' || shareData.author === 'Unknown Author') 
            ? video.author 
            : (shareData.author || video.author);

          const resolvedOwnerAvatarUrl = (!shareData.ownerAvatarUrl)
            ? video.ownerAvatarUrl
            : shareData.ownerAvatarUrl;

          const normalizedShare = {
            ...shareData,
            author: resolvedAuthor,
            ownerAvatarUrl: resolvedOwnerAvatarUrl,
            hydrationPending: false,
            isHydrationPlaceholder: false,
            generationRecords: episodeData?.generationRecords,
            content: {
              ...shareDataContent,
              author: resolvedAuthor,
              ownerAvatarUrl: resolvedOwnerAvatarUrl,
              hydrationPending: false,
              isHydrationPlaceholder: false,
              generationRecords: episodeData?.generationRecords,
            },
          };
          
          setCurrentEpisode(normalizedShare);
          setCleanReadData(
            (normalizedShare?.content as any)?.cleanRead ??
              shareDataContent?.cleanRead ??
              null,
          );
        } else if (response.status === 404) {
          const audioResponse = await fetch(
            `/api/youtube/audio-url?videoId=${shareId}`,
          );
          const audioPayload = await audioResponse.json().catch(() => ({}));
          if (!audioResponse.ok || typeof audioPayload?.file !== "string") {
            throw new Error(
              audioPayload?.error || `Failed to fetch audio url (${audioResponse.status})`,
            );
          }
          const fallbackEpisode = {
            ...placeholder,
            hydrationPending: false,
            isHydrationPlaceholder: false,
            audioUrl: audioPayload.file,
            content: {
              ...(placeholder.content || {}),
              hydrationPending: false,
              isHydrationPlaceholder: false,
              audioUrl: audioPayload.file,
            },
          };
          setCurrentEpisode(fallbackEpisode);
          setCleanReadData(null);
        } else {
          throw new Error(`Failed to fetch episode data: ${response.status}`);
        }
      } catch (error) {
        console.error("Unable to open episode in global player:", error);
        closeFullPlayer();
      } finally {
        setPlayerLoading(false);
      }
    },
    [
      closeFullPlayer,
      openFullPlayer,
      session?.access_token,
      setCleanReadData,
      setCurrentEpisode,
      setPlayerLoading,
      showPlayer,
    ],
  );

  // 处理卡片点击：桌面端跳转，移动端直接播放
  const handleCardClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>, video: YouTubeVideo) => {
      // 桌面端：让 Link 默认跳转行为生效
      if (!allowDirectPlay) {
        return;
      }

      // 移动端：阻止默认跳转，直接播放
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }

      e.preventDefault();
      await handlePlayYouTubeVideo(video);
    },
    [allowDirectPlay, handlePlayYouTubeVideo]
  );

  // 🔥 优化：在 mounted 之前或无法访问时显示 skeleton，而不是返回 null
  // 这样用户能立即看到加载状态，提升用户体验
  const showSkeleton = !isMounted || isLoadingYoutube;
  const shouldShow = isMounted && canAccessYouTube;

  // 如果已 mounted 但无法访问 YouTube，不显示组件
  if (isMounted && !canAccessYouTube) {
    return null;
  }

  return (
    <section className="mt-4">
      <header className="pl-2 pb-1 sm:pb-2 flex items-center justify-between">
        <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.youtube.title')}</h2>
      </header>
      {/* Mobile & Desktop: 横向滚动，统一使用与 recently-watched-section 相同的卡片样式 */}
      <div className="w-full overflow-x-auto scrollbar-hide pb-4 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
        <div className="flex gap-1 sm:gap-4" style={{ width: 'fit-content' }}>
          {showSkeleton ? (
            FEATURED_YOUTUBE_VIDEO_IDS.map((id) => (
              <div key={id} className="flex-shrink-0 w-[160px] sm:w-[200px]">
                <div className="h-28 sm:h-36 animate-pulse rounded-lg bg-muted/40 mb-2" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted/40 mb-1" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted/40" />
              </div>
            ))
          ) : (
            shouldShow && youtubeVideos.map((video) => (
              <Link
                key={video.videoId}
                href={getEpisodeUrl(video.videoId)}
                onClick={(e) => handleCardClick(e, video)}
                className="flex-shrink-0 w-[160px] sm:w-[200px] group border border-border/60 rounded-xl block"
              >
                <div 
                  className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted border border-border/60 transition-all hover:border-primary cursor-pointer"
                >
                  {video.thumbnail ? (
                    <Image
                      src={video.thumbnail}
                      alt={video.title}
                      fill
                      className="object-cover transition duration-300 group-hover:scale-105"
                      sizes="(max-width: 768px) 160px, 200px"
                      loading="lazy"
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground text-xs p-2 text-center">
                      {video.title}
                    </div>
                  )}
                  
                </div>

                <div className="mt-2 mb-1 space-y-1 px-1 sm:px-2">
                  <h3 className="line-clamp-2 text-[12px] sm:text-[14px] font-semibold leading-tight group-hover:text-primary transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {video.ownerAvatarUrl ? (
                      <Image
                        src={video.ownerAvatarUrl}
                        alt={video.author}
                        width={20}
                        height={20}
                        className="rounded-full w-3.5 h-3.5 sm:w-5 sm:h-5"
                        loading="lazy"
                      />
                    ) : null}
                    <p className="line-clamp-1 truncate">
                      {video.author || t('home.youtube.defaultCreator')}
                    </p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

